import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "./api";
import { PageHeader, TextInput, TextArea, Button, Card } from "./ui";
import { Save, Plus, Trash2 } from "lucide-react";
import { usePublicData } from "@/hooks/usePublicData";

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { reload: reloadPublic } = usePublicData();

  useEffect(() => {
    api.get("/admin/settings").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const upd = (k, v) => setData((s) => ({ ...s, [k]: v }));
  const updMetric = (i, k, v) => {
    const m = [...(data.metrics || [])];
    m[i] = { ...m[i], [k]: v };
    upd("metrics", m);
  };
  const addMetric = () => upd("metrics", [...(data.metrics || []), { k: "", v: "", v_en: "" }]);
  const removeMetric = (i) => upd("metrics", data.metrics.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { data });
      await reloadPublic?.();
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Yükleniyor…</div>;

  return (
    <div>
      <PageHeader
        title="Site Ayarları"
        description="Marka, iletişim, hero, SEO ve metrikleri buradan yönetin."
        right={<Button onClick={save} disabled={busy} data-testid="settings-save"><Save className="w-4 h-4" /> Kaydet</Button>}
      />

      <div className="space-y-8">
        <Section title="Marka & İletişim">
          <Grid2>
            <TextInput label="Marka Adı" value={data.brand || ""} onChange={(e) => upd("brand", e.target.value)} testid="s-brand" />
            <TextInput label="Logo Metni" value={data.logo_text || ""} onChange={(e) => upd("logo_text", e.target.value)} testid="s-logo-text" placeholder="DEKOSTA3D" />
            <TextInput label="Logo Görsel URL" value={data.logo_image_url || ""} onChange={(e) => upd("logo_image_url", e.target.value)} testid="s-logo-url" placeholder="https://..." />
            <div />
            <TextInput label="Kısa Tagline" value={data.tagline || ""} onChange={(e) => upd("tagline", e.target.value)} testid="s-tagline" />
            <TextInput label="Telefon" value={data.phone || ""} onChange={(e) => upd("phone", e.target.value)} testid="s-phone" />
            <TextInput label="E-posta" value={data.email || ""} onChange={(e) => upd("email", e.target.value)} testid="s-email" />
            <TextInput label="WhatsApp Numarası (uluslararası, boşluksuz)" value={data.whatsapp_number || ""} onChange={(e) => upd("whatsapp_number", e.target.value)} testid="s-wa-num" placeholder="905551234567" />
            <TextInput label="WhatsApp Görünen" value={data.whatsapp_display || ""} onChange={(e) => upd("whatsapp_display", e.target.value)} testid="s-wa-display" placeholder="+90 555 123 45 67" />
            <div>
              <TextInput label="Adres (isteğe bağlı)" value={data.address || ""} onChange={(e) => upd("address", e.target.value)} testid="s-address" />
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 leading-relaxed">
                Evden/atölyesiz üretimde tam adres paylaşmak istemiyorsanız boş bırakın; iletişim ve footer&apos;da adres satırı görünmez. İsterseniz yalnızca şehir veya teslimat notu girebilirsiniz.
              </p>
            </div>
            <TextInput label="Çalışma Saatleri" value={data.working_hours || ""} onChange={(e) => upd("working_hours", e.target.value)} testid="s-hours" />
          </Grid2>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            Logo gorsel URL doluysa ikon yerine gorsel kullanilir. Bos birakirsan mevcut turuncu kutu logo devam eder.
          </p>
        </Section>

        <Section title="Anasayfa Hero">
          <Grid2>
            <TextInput label="Hero Eyebrow (TR)" value={data.hero_eyebrow || ""} onChange={(e) => upd("hero_eyebrow", e.target.value)} testid="s-hero-eyebrow" />
            <TextInput label="Hero Eyebrow (EN)" value={data.hero_eyebrow_en || ""} onChange={(e) => upd("hero_eyebrow_en", e.target.value)} />
            <TextInput label="Başlık 1. Satır (TR)" value={data.hero_title_line1 || ""} onChange={(e) => upd("hero_title_line1", e.target.value)} testid="s-hero-l1" />
            <TextInput label="Başlık 1. Satır (EN)" value={data.hero_title_line1_en || ""} onChange={(e) => upd("hero_title_line1_en", e.target.value)} />
            <TextInput label="Başlık 2. Satır — turuncu (TR)" value={data.hero_title_line2 || ""} onChange={(e) => upd("hero_title_line2", e.target.value)} testid="s-hero-l2" />
            <TextInput label="Başlık 2. Satır (EN)" value={data.hero_title_line2_en || ""} onChange={(e) => upd("hero_title_line2_en", e.target.value)} />
            <TextInput label="Başlık 3. Satır (TR)" value={data.hero_title_line3 || ""} onChange={(e) => upd("hero_title_line3", e.target.value)} testid="s-hero-l3" />
            <TextInput label="Başlık 3. Satır (EN)" value={data.hero_title_line3_en || ""} onChange={(e) => upd("hero_title_line3_en", e.target.value)} />
          </Grid2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextArea label="Alt Başlık (TR)" rows={3} value={data.hero_subtitle || ""} onChange={(e) => upd("hero_subtitle", e.target.value)} testid="s-hero-subtitle" />
            <TextArea label="Alt Başlık (EN)" rows={3} value={data.hero_subtitle_en || ""} onChange={(e) => upd("hero_subtitle_en", e.target.value)} />
          </div>
        </Section>

        <Section title="Güven Metrikleri">
          <div className="space-y-3" data-testid="s-metrics">
            {(data.metrics || []).map((m, i) => (
              <div key={i} className="flex flex-col md:flex-row md:items-center gap-3">
                <TextInput className="flex-1 md:max-w-[120px]" value={m.k} onChange={(e) => updMetric(i, "k", e.target.value)} placeholder="24sa" />
                <TextInput className="flex-[2]" value={m.v} onChange={(e) => updMetric(i, "v", e.target.value)} placeholder="Metin (TR)" />
                <TextInput className="flex-[2]" value={m.v_en || ""} onChange={(e) => updMetric(i, "v_en", e.target.value)} placeholder="Metin (EN, opsiyonel)" />
                <button type="button" onClick={() => removeMetric(i)} className="p-2 text-zinc-400 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Button variant="secondary" onClick={addMetric} data-testid="s-metric-add"><Plus className="w-4 h-4" /> Metrik Ekle</Button>
          </div>
        </Section>

        <Section title="SEO">
          <Grid2>
            <TextInput label="Sayfa Başlığı (TR)" value={data.seo_title || ""} onChange={(e) => upd("seo_title", e.target.value)} testid="s-seo-title" />
            <TextInput label="Sayfa Başlığı (EN)" value={data.seo_title_en || ""} onChange={(e) => upd("seo_title_en", e.target.value)} />
          </Grid2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextArea label="Meta Description (TR)" rows={3} value={data.seo_description || ""} onChange={(e) => upd("seo_description", e.target.value)} testid="s-seo-desc" />
            <TextArea label="Meta Description (EN)" rows={3} value={data.seo_description_en || ""} onChange={(e) => upd("seo_description_en", e.target.value)} />
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card className="p-6 md:p-8">
      <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-6 text-[#FF4F00]">{title}</h2>
      {children}
    </Card>
  );
}
function Grid2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }
